/**
 * CopilotComposer — área de entrada de mensagens do copiloto (TASK-21).
 *
 * Campo de texto multilinha com envio por Enter (Shift+Enter = nova linha).
 * Desabilitado durante loading e responding (previne duplo-envio).
 * Suporte completo a navegação por teclado (RNF 9.3).
 *
 * Referências: design.md §20.15, Req 9, RNF 9.3, TASK-21.
 */

import { useState, useRef, type KeyboardEvent } from "react";
import { Icon } from "@azim-crm/icons";
import styles from "./Copilot.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CopilotComposerProps {
  /** Chamado ao enviar a mensagem (Enter sem Shift). */
  onSend(text: string): void;
  /** Bloqueia envio durante loading/responding (previne duplo-envio). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Composer com textarea e botão de envio.
 * Enter sem Shift envia; Shift+Enter insere nova linha.
 */
export function CopilotComposer({ onSend, disabled = false }: CopilotComposerProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue("");
    // Restaura height dinâmico
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    // Auto-resize da textarea até max-height definido no CSS.
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  };

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className={styles.composer}>
      <textarea
        ref={textareaRef}
        className={styles.composerTextarea}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        placeholder="Pergunte ao Azim AI…"
        aria-label="Mensagem para o Azim AI"
        aria-multiline="true"
        rows={1}
        disabled={disabled}
      />
      <button
        type="button"
        className={styles.composerSend}
        onClick={handleSend}
        disabled={!canSend}
        aria-label="Enviar mensagem"
        title="Enviar (Enter)"
      >
        <Icon name="arrowUp" size={16} />
      </button>
    </div>
  );
}
